/**
 * InventoryOverlay.js
 *
 * Phase 6.1 — In-game inventory overlay.
 *
 * Renders a backdrop-blurred modal over the live 3D scene without leaving the
 * /play route. Reuses InventoryView so the cargo + death-cache rendering is
 * identical to the standalone /inventory page.
 *
 * Lifecycle:
 *   - Parent (Explore3D) toggles `open` via the `I` key, Escape, the HUD
 *     mini-panel button, or the close button here.
 *   - On open, the parent calls `scene.setInputPaused(true)` so movement keys
 *     do not drift. We also auto-refresh data each time the overlay opens.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Package, X } from 'lucide-react';
import InventoryView from './InventoryView';

export function InventoryOverlay({ open, onClose }) {
  const { api } = useAuth();
  const [inventory, setInventory] = useState(null);
  const [deaths, setDeaths] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, d] = await Promise.all([
        api.get('/api/player/inventory').then((r) => r.data),
        api.get('/api/player/deaths').then((r) => r.data),
      ]);
      setInventory(i);
      setDeaths(Array.isArray(d) ? d : []);
    } catch (_e) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Reload each time the overlay opens (cheap, ensures fresh data)
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const dropOre = useCallback(async (symbol) => {
    const ores = (inventory?.ores || []).filter((o) => o.symbol !== symbol);
    try {
      const { data } = await api.put('/api/player/inventory', { ores });
      setInventory(data);
      toast.success('Ore stack discarded');
    } catch (_e) {
      toast.error('Failed to drop ore');
    }
  }, [api, inventory]);

  const clearAll = useCallback(async () => {
    try {
      const { data } = await api.put('/api/player/inventory', { ores: [] });
      setInventory(data);
      toast.success('Cargo cleared');
    } catch (_e) {
      toast.error('Failed to clear cargo');
    }
  }, [api]);

  const recoverCache = useCallback(async (cache) => {
    try {
      await api.post(`/api/player/deaths/${cache.id}/recover`, {
        pos: null,
        coords_key: cache.coords_key,
        planet_idx: cache.planet_idx,
        moon_idx: cache.moon_idx,
      });
      toast.success('Cache recovered');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to recover cache';
      toast.error(detail);
    }
  }, [api, load]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center"
      data-testid="inventory-overlay"
      // Block pointer events from reaching the canvas underneath
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#02060f]/85 backdrop-blur-md" />

      {/* Modal panel */}
      <div
        className="relative w-[min(960px,94vw)] max-h-[88vh] overflow-y-auto rounded-2xl border border-cyan-400/30 bg-[#02060f]/95 shadow-[0_0_0_1px_rgba(24,210,255,0.18)] p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="inventory-overlay-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-cyan-300" />
            <div>
              <h2
                id="inventory-overlay-title"
                className="text-2xl font-bold text-slate-100"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Inventory
              </h2>
              <p className="text-[11px] text-cyan-300/70 tracking-[0.2em] uppercase mt-0.5">
                Press <span className="text-cyan-200">I</span> or <span className="text-cyan-200">Esc</span> to close
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            data-testid="inventory-overlay-close"
          >
            <X className="w-4 h-4 mr-2" /> Close
          </Button>
        </div>

        <InventoryView
          loading={loading}
          inventory={inventory}
          deaths={deaths}
          onDropOre={dropOre}
          onClearAll={clearAll}
          onRecoverCache={recoverCache}
        />
      </div>
    </div>
  );
}

export default InventoryOverlay;
